import React, { useState } from 'react';
import QRCode from 'react-qr-code';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { CreditCard, CheckCircle2, X, Clock, MapPin, Shield, Radio, ChevronRight } from 'lucide-react';

const VENDOR_LOGOS = [
  { name: 'SKIDATA', url: 'https://customer-assets.emergentagent.com/job_826be93c-f208-4e95-b8c7-1605ad035c2f/artifacts/xjckq956_SKIDATA_logo.svg%20%281%29.png' },
  { name: 'TIBA Parking', url: 'https://customer-assets.emergentagent.com/job_826be93c-f208-4e95-b8c7-1605ad035c2f/artifacts/2sq30ykw_tiba-3.png' },
  { name: 'Flash Parking', url: 'https://customer-assets.emergentagent.com/job_826be93c-f208-4e95-b8c7-1605ad035c2f/artifacts/yzn9qvl8_Flash-Logo_Website_25.png' },
  { name: 'Amano McGann', url: 'https://customer-assets.emergentagent.com/job_826be93c-f208-4e95-b8c7-1605ad035c2f/artifacts/mcqb3jat_AM_logo.png' },
  { name: 'ParkMobile', url: 'https://customer-assets.emergentagent.com/job_826be93c-f208-4e95-b8c7-1605ad035c2f/artifacts/jxfbeglm_powered-by-parkmobile.svg' },
];

export default function ReservationModal({ garage, open, onClose, primaryColor }) {
  const [step, setStep] = useState(1);
  const [selectedArea, setSelectedArea] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [reservationTime] = useState(new Date());

  if (!open || !garage) return null;

  const color = primaryColor || '#3b82f6';
  const areas = (garage.attributes || []).filter(a => a.availableSpaces > 0);

  const handleConfirmPayment = () => {
    setProcessing(true);
    setTimeout(() => {
      setProcessing(false);
      setStep(3);
    }, 1500);
  };

  const handleClose = () => {
    setStep(1);
    setSelectedArea(null);
    setProcessing(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4" data-testid="reservation-modal">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white rounded-t-2xl border-b border-slate-100 px-6 py-4 flex items-center justify-between z-10">
          <div>
            <h2 className="text-lg font-bold text-slate-800" data-testid="reservation-modal-title">
              {step === 1 && 'Reserve Parking Space'}
              {step === 2 && 'Payment Details'}
              {step === 3 && 'Reservation Confirmed'}
            </h2>
            <p className="text-sm text-slate-500">{garage.name}</p>
          </div>
          <button onClick={handleClose} className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100" data-testid="reservation-close-btn">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-5">
          {/* Step indicator */}
          <div className="flex items-center justify-center mb-6" data-testid="step-indicator">
            {[1, 2, 3].map((s, i) => (
              <React.Fragment key={s}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${
                  step >= s ? 'text-white' : 'bg-slate-100 text-slate-400'
                }`} style={step >= s ? { backgroundColor: color } : {}}>
                  {step > s ? <CheckCircle2 className="w-4 h-4" /> : s}
                </div>
                {i < 2 && <div className={`w-12 h-0.5 mx-1 ${step > s ? 'bg-green-400' : 'bg-slate-200'}`} />}
              </React.Fragment>
            ))}
          </div>

          {/* STEP 1: Select Area */}
          {step === 1 && (
            <div className="space-y-4" data-testid="step-1-select">
              <p className="text-sm text-slate-600 mb-3">Select an area to reserve your space:</p>
              {areas.length > 0 ? (
                <div className="space-y-2">
                  {areas.map((area, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => setSelectedArea(area)}
                      className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                        selectedArea?.name === area.name
                          ? 'border-blue-500 bg-blue-50 shadow-sm'
                          : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                      }`}
                      data-testid={`area-option-${idx}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-sm font-semibold text-slate-800">
                            Reserve Space in {area.name}
                          </div>
                          <div className="text-xs text-slate-500 mt-0.5">
                            {area.availableSpaces} of {area.totalSpaces} spaces available
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Badge className="bg-green-100 text-green-700 text-xs">{area.availableSpaces} avail</Badge>
                          <ChevronRight className={`w-4 h-4 transition-colors ${selectedArea?.name === area.name ? 'text-blue-500' : 'text-slate-300'}`} />
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 text-slate-500 text-sm">No available spaces at this location.</div>
              )}
              <Button
                onClick={() => setStep(2)}
                disabled={!selectedArea}
                className="w-full text-white mt-4"
                style={{ backgroundColor: color }}
                data-testid="continue-to-payment-btn"
              >
                Continue to Payment <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          )}

          {/* STEP 2: Payment Form */}
          {step === 2 && (
            <div className="space-y-4" data-testid="step-2-payment">
              {/* Selected area summary */}
              <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wide font-medium">Reserving</p>
                    <p className="text-sm font-bold text-slate-800 mt-0.5">{selectedArea?.name} — {garage.name}</p>
                  </div>
                  <Badge className="bg-green-100 text-green-700">{selectedArea?.availableSpaces} avail</Badge>
                </div>
              </div>

              {/* Pre-populated card form */}
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1 block">Cardholder Name</label>
                  <Input value="John Smith" readOnly className="bg-slate-50 text-slate-700" data-testid="card-name-input" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1 block">Card Number</label>
                  <div className="relative">
                    <Input value="4242  4242  4242  4242" readOnly className="bg-slate-50 text-slate-700 pl-10" data-testid="card-number-input" />
                    <CreditCard className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-slate-600 mb-1 block">Expiry</label>
                    <Input value="12/28" readOnly className="bg-slate-50 text-slate-700" data-testid="card-expiry-input" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-600 mb-1 block">CVV</label>
                    <Input value="***" readOnly className="bg-slate-50 text-slate-700" data-testid="card-cvv-input" />
                  </div>
                </div>
              </div>

              <div className="flex items-start space-x-2 bg-blue-50 border border-blue-200 rounded-lg p-3">
                <Shield className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-blue-700">
                  Card will be saved on file. Payment will be billed for time parked in area. Space is reserved upon confirmation.
                </p>
              </div>

              <div className="flex space-x-3 mt-4">
                <Button variant="outline" onClick={() => setStep(1)} className="flex-1" data-testid="back-to-select-btn">
                  Back
                </Button>
                <Button
                  onClick={handleConfirmPayment}
                  disabled={processing}
                  className="flex-1 text-white"
                  style={{ backgroundColor: color }}
                  data-testid="confirm-payment-btn"
                >
                  {processing ? (
                    <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />Processing...</>
                  ) : (
                    'Confirm Reservation'
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* STEP 3: Success */}
          {step === 3 && (
            <div className="space-y-5" data-testid="step-3-success">
              {/* Success banner */}
              <div className="text-center">
                <div className="w-16 h-16 rounded-full mx-auto flex items-center justify-center mb-3" style={{ backgroundColor: `${color}15` }}>
                  <CheckCircle2 className="w-9 h-9" style={{ color }} />
                </div>
                <h3 className="text-lg font-bold text-slate-800">Reservation Confirmed</h3>
                <p className="text-sm text-slate-500 mt-1">Your parking space has been reserved</p>
              </div>

              {/* Ticket details */}
              <div className="bg-slate-50 rounded-xl border border-slate-200 divide-y divide-slate-200" data-testid="ticket-details">
                <div className="px-4 py-3 flex items-center justify-between">
                  <span className="text-xs text-slate-500 flex items-center"><MapPin className="w-3.5 h-3.5 mr-1.5" />Space</span>
                  <span className="text-sm font-semibold text-slate-800">{selectedArea?.name} — {garage.name}</span>
                </div>
                <div className="px-4 py-3 flex items-center justify-between">
                  <span className="text-xs text-slate-500 flex items-center"><Clock className="w-3.5 h-3.5 mr-1.5" />Time Reserved</span>
                  <span className="text-sm font-semibold text-slate-800">{reservationTime.toLocaleString()}</span>
                </div>
                <div className="px-4 py-3 flex items-center justify-between">
                  <span className="text-xs text-slate-500 flex items-center"><CreditCard className="w-3.5 h-3.5 mr-1.5" />Payment</span>
                  <span className="text-sm font-semibold text-slate-800">Visa ending 4242</span>
                </div>
              </div>

              {/* QR Code */}
              <div className="flex flex-col items-center py-2" data-testid="reservation-qr-code">
                <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm">
                  <QRCode
                    value={`PARKING-RES|${garage.name}|${selectedArea?.name}|${reservationTime.toISOString()}|VISA-4242|CONFIRMED`}
                    size={140}
                    level="M"
                    fgColor="#1e293b"
                  />
                </div>
                <p className="text-[10px] text-slate-400 mt-2 font-medium">Scan at garage entrance</p>
              </div>

              {/* Billing note */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-2">
                <div className="flex items-start space-x-2">
                  <Shield className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-blue-800 font-medium">
                    Card saved on file. Payment will be billed for time parked in {selectedArea?.name}. Space is reserved in {selectedArea?.name}.
                  </p>
                </div>
              </div>

              {/* PARCS system integration */}
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 space-y-2" data-testid="parcs-notice">
                <div className="flex items-start space-x-2">
                  <Radio className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-emerald-800 font-semibold">Space deducted from signage</p>
                    <p className="text-xs text-emerald-700 mt-0.5">Information sent to PARCS system. Reserved space count updated across all connected guidance systems.</p>
                  </div>
                </div>
              </div>

              {/* Vendor logos */}
              <div data-testid="vendor-logos">
                <p className="text-[10px] text-slate-400 uppercase tracking-wider font-medium text-center mb-3">Connected Systems</p>
                <div className="flex items-center justify-center gap-4 flex-wrap">
                  {VENDOR_LOGOS.map((v) => (
                    <div key={v.name} className="h-10 w-auto flex items-center justify-center bg-white rounded-lg border border-slate-100 px-3 py-1.5 shadow-sm" title={v.name}>
                      <img src={v.url} alt={v.name} className="h-7 w-auto object-contain max-w-[80px]" />
                    </div>
                  ))}
                </div>
              </div>

              <Button onClick={handleClose} className="w-full text-white" style={{ backgroundColor: color }} data-testid="done-btn">
                Done
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
